import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import {
  fetchRoomBoard, updateRoomStatus, fetchRoomTypes,
  uploadRoomImages, deleteRoomImage, fetchRoomImages,
} from '../../services/api.js';
import { useOfflineInvalidation } from '../../components/ui.jsx';

// ─── Status Configuration ─────────────────────────────────────────────────────
const STATUS_META = {
  available:   { color: '#1A7A4A', bg: '#EAF7EE', label: 'Available',   short: 'AVAIL' },
  occupied:    { color: '#C0392B', bg: '#FDECEA', label: 'Occupied',     short: 'OCC'   },
  dirty:       { color: '#E07B00', bg: '#FEF5E7', label: 'Dirty',        short: 'DIRTY' },
  inspected:   { color: '#1A7F8E', bg: '#E6F4F6', label: 'Inspected',   short: 'INSP'  },
  maintenance: { color: '#6B7A8D', bg: '#F4F6F8', label: 'Maintenance', short: 'MAINT' },
  blocked:     { color: '#3A4A5C', bg: '#EDF2F7', label: 'Blocked',     short: 'BLKD'  },
};

const MAX_IMAGES = 10;

/** Parse JSON or legacy pipe/comma image list → string[] */
function parseImages(raw) {
  if (raw == null || raw === false) return [];
  if (Array.isArray(raw)) return raw.filter(v => v && typeof v === 'string');
  if (typeof raw !== 'string') return [];
  if (!raw) return [];
  try {
    const p = JSON.parse(raw);
    if (Array.isArray(p)) return p.filter(v => v && typeof v === 'string');
    if (p && typeof p === 'string') return [p];
    return [];
  } catch (_) {
    if (raw.includes('|')) return raw.split('|').map(s => s.trim()).filter(Boolean);
    if (raw.includes(',')) return raw.split(',').map(s => s.trim()).filter(Boolean);
    return raw.trim() ? [raw.trim()] : [];
  }
}

const getFilename = url => url.split('/').pop();

// ─── Lightbox ─────────────────────────────────────────────────────────────────
function Lightbox({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx(i => (i - 1 + images.length) % images.length);
  const next = () => setIdx(i => (i + 1) % images.length);

  useEffect(() => {
    const handler = e => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,.92)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn .15s ease',
    }}>
      <button onClick={onClose} style={{
        position: 'absolute', top: 20, right: 24, background: 'rgba(255,255,255,.12)',
        border: 'none', borderRadius: '50%', width: 40, height: 40,
        color: 'white', fontSize: 20, cursor: 'pointer', zIndex: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>✕</button>
      <div style={{
        position: 'absolute', top: 24, left: '50%', transform: 'translateX(-50%)',
        color: 'rgba(255,255,255,.7)', fontSize: 13, fontFamily: FONTS.body,
      }}>{idx + 1} / {images.length}</div>
      <img src={images[idx]} alt={`Photo ${idx + 1}`} onClick={e => e.stopPropagation()}
        style={{ maxWidth: 'min(92vw, 1100px)', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 24px 80px rgba(0,0,0,.6)' }}
      />
      {images.length > 1 && (
        <>
          <button onClick={e => { e.stopPropagation(); prev(); }} style={{
            position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(255,255,255,.12)', border: 'none', borderRadius: '50%',
            width: 48, height: 48, color: 'white', fontSize: 24, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)',
          }}>‹</button>
          <button onClick={e => { e.stopPropagation(); next(); }} style={{
            position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(255,255,255,.12)', border: 'none', borderRadius: '50%',
            width: 48, height: 48, color: 'white', fontSize: 24, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)',
          }}>›</button>
        </>
      )}
      {images.length > 1 && (
        <div style={{
          position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', gap: 6, padding: '8px 12px',
          background: 'rgba(0,0,0,.45)', borderRadius: 10, backdropFilter: 'blur(8px)',
          maxWidth: '90vw', overflowX: 'auto',
        }}>
          {images.map((img, i) => (
            <img key={i} src={img} alt="" onClick={e => { e.stopPropagation(); setIdx(i); }}
              style={{
                width: 52, height: 38, objectFit: 'cover', borderRadius: 5,
                cursor: 'pointer', flexShrink: 0,
                border: i === idx ? `2px solid ${T.gold}` : '2px solid transparent',
                opacity: i === idx ? 1 : 0.55, transition: 'opacity .15s, border-color .15s',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Image Manager Modal ───────────────────────────────────────────────────────
function ImageManagerModal({ room, roomType, onClose, onUpdate }) {
  const [images, setImages]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting]   = useState(null);
  const [lightbox, setLightbox]   = useState(null);
  const [dragOver, setDragOver]   = useState(false);
  const [error, setError]         = useState('');
  const fileRef = useRef();
  const roomId = room.roomId;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchRoomImages(roomId);
      setImages(res.images || []);
    } catch (e) {
      setImages(parseImages(room.images));
    } finally { setLoading(false); }
  }, [roomId]);

  useEffect(() => { load(); }, [load]);

  const handleFiles = async (files) => {
    const arr = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!arr.length) return;
    if (images.length + arr.length > MAX_IMAGES) { setError(`Max ${MAX_IMAGES} images allowed.`); return; }
    setUploading(true); setError('');
    try {
      const res = await uploadRoomImages(roomId, arr);
      const updated = res.images || [];
      setImages(updated); onUpdate(roomId, updated);
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  };

  const handleDelete = async (url) => {
    const fname = getFilename(url);
    setDeleting(fname); setError('');
    try {
      const res = await deleteRoomImage(roomId, fname);
      const updated = res.images || images.filter(u => u !== url);
      setImages(updated); onUpdate(roomId, updated);
    } catch (e) { setError(e.message); }
    finally { setDeleting(null); }
  };

  const rtImages = parseImages(roomType?.images);
  const meta = STATUS_META[room.status] || STATUS_META.available;

  return (
    <>
      {lightbox !== null && (
        <Lightbox images={images.length ? images : rtImages} startIndex={lightbox} onClose={() => setLightbox(null)} />
      )}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(13,27,42,.6)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 20,
        backdropFilter: 'blur(3px)',
      }} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={{
          background: T.white, borderRadius: 18, width: '100%', maxWidth: 680,
          maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: SHADOW.xl,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '18px 24px', borderBottom: `1px solid ${T.border}`, background: T.surface,
          }}>
            <div>
              <h3 style={{ fontFamily: FONTS.display, fontSize: 20, fontWeight: 700, color: T.navy }}>
                Room {room.roomNumber} — Photos
              </h3>
              <p style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                <span style={{ color: meta.color, fontWeight: 700 }}>● {meta.label}</span>
                {roomType && ` · ${roomType.name}`}
                {room.floor && ` · Floor ${room.floor}`}
              </p>
            </div>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', fontSize: 22,
              cursor: 'pointer', color: T.muted, lineHeight: 1, padding: '4px 8px',
            }}>×</button>
          </div>

          <div style={{ overflowY: 'auto', flex: 1, padding: 24 }}>
            {rtImages.length > 0 && images.length === 0 && (
              <div style={{
                background: T.tealPale, border: `1px solid ${T.teal}30`,
                borderRadius: 10, padding: '10px 14px', marginBottom: 18,
                fontSize: 12, color: T.teal,
              }}>
                ℹ️ No room-specific photos yet — room type photos shown for reference.
              </div>
            )}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? T.teal : T.border}`,
                borderRadius: 12, padding: '28px 20px', textAlign: 'center',
                cursor: 'pointer', marginBottom: 20, transition: 'all .2s',
                background: dragOver ? T.tealPale : T.surface,
              }}>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden
                onChange={e => handleFiles(e.target.files)} />
              {uploading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 28, height: 28, border: `3px solid ${T.border}`,
                    borderTopColor: T.teal, borderRadius: '50%',
                    animation: 'spin .7s linear infinite',
                  }} />
                  <p style={{ fontSize: 13, color: T.teal, fontFamily: FONTS.body }}>Uploading…</p>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: T.navy, fontFamily: FONTS.body }}>
                    Drop photos here or click to browse
                  </p>
                  <p style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
                    JPEG, PNG, WEBP · Max 10MB each · {images.length}/{MAX_IMAGES} used
                  </p>
                </>
              )}
            </div>

            {error && (
              <div style={{
                background: '#FEF2F2', border: `1px solid #FCA5A5`,
                borderRadius: 8, padding: '10px 14px', marginBottom: 14,
                fontSize: 12, color: T.danger,
              }}>⚠ {error}</div>
            )}

            {loading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ height: 120, borderRadius: 10, background: T.border }} className="shimmer" />
                ))}
              </div>
            ) : images.length > 0 ? (
              <>
                <p style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: .6, marginBottom: 10, fontFamily: FONTS.body }}>
                  Room Photos ({images.length})
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                  {images.map((url, i) => (
                    <div key={url} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: `1px solid ${T.border}`, aspectRatio: '4/3' }}
                      onMouseEnter={e => e.currentTarget.querySelector('.img-overlay').style.opacity = '1'}
                      onMouseLeave={e => e.currentTarget.querySelector('.img-overlay').style.opacity = '0'}>
                      <img src={url} alt={`Room ${room.roomNumber} photo ${i + 1}`} onClick={() => setLightbox(i)}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in', display: 'block' }}
                        onError={e => { e.target.style.background = T.border; e.target.style.opacity = .4; }}
                      />
                      {i === 0 && (
                        <div style={{ position: 'absolute', top: 6, left: 6, background: T.navy, borderRadius: 5, padding: '2px 7px', fontSize: 9, color: 'white', fontWeight: 700, fontFamily: FONTS.body }}>COVER</div>
                      )}
                      <div className="img-overlay" style={{
                        position: 'absolute', inset: 0, background: 'rgba(0,0,0,.42)', opacity: 0,
                        transition: 'opacity .2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      }}>
                        <button onClick={() => setLightbox(i)} style={{
                          background: 'rgba(255,255,255,.2)', border: '1px solid rgba(255,255,255,.4)',
                          borderRadius: 7, padding: '5px 10px', color: 'white', cursor: 'pointer', fontSize: 11, fontFamily: FONTS.body, backdropFilter: 'blur(4px)',
                        }}>🔍 View</button>
                        <button onClick={() => handleDelete(url)} disabled={deleting === getFilename(url)}
                          style={{
                            background: 'rgba(192,57,43,.7)', border: '1px solid rgba(255,100,80,.4)',
                            borderRadius: 7, padding: '5px 10px', color: 'white', cursor: 'pointer', fontSize: 11, fontFamily: FONTS.body, backdropFilter: 'blur(4px)',
                          }}>
                          {deleting === getFilename(url) ? '…' : '🗑 Remove'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <p style={{ fontSize: 32, marginBottom: 8 }}>🖼</p>
                <p style={{ fontSize: 14, color: T.muted, fontFamily: FONTS.body }}>No room photos yet</p>
              </div>
            )}

            {rtImages.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: .6, marginBottom: 10, fontFamily: FONTS.body }}>
                  Room Type Photos ({rtImages.length}) — reference only
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                  {rtImages.map((url, i) => (
                    <div key={i} style={{ borderRadius: 8, overflow: 'hidden', aspectRatio: '4/3', border: `1px solid ${T.border}`, opacity: .65 }}>
                      <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{
            padding: '14px 24px', borderTop: `1px solid ${T.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: T.surface,
          }}>
            <p style={{ fontSize: 12, color: T.muted, fontFamily: FONTS.body }}>
              {images.length > 0 ? `${images.length} photo${images.length > 1 ? 's' : ''} · first is the cover` : 'Upload photos for this room'}
            </p>
            <button onClick={onClose} style={{
              background: T.navy, color: T.white, border: 'none',
              borderRadius: 9, padding: '9px 20px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: FONTS.body,
            }}>Done</button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Occupancy Summary ─────────────────────────────────────────────────────────
function OccupancySummary({ rooms }) {
  if (!rooms.length) return null;
  const total = rooms.length;
  const counts = Object.keys(STATUS_META).reduce((acc, s) => {
    acc[s] = rooms.filter(r => r.status === s).length; return acc;
  }, {});
  const pct = Math.round(((counts.occupied || 0) / total) * 100);

  return (
    <div style={{
      background: T.navy, borderRadius: 16, padding: '20px 24px 18px',
      marginBottom: 20, position: 'relative', overflow: 'hidden',
    }}>
      {/* Decorative circles */}
      <div style={{ position: 'absolute', right: -10, top: -30, width: 130, height: 130, borderRadius: '50%', background: `${T.gold}08`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', right: 60, top: -50, width: 90, height: 90, borderRadius: '50%', background: `${T.gold}05`, pointerEvents: 'none' }} />

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 28, flexWrap: 'wrap' }}>
        {/* Big occupancy stat */}
        <div style={{ minWidth: 150 }}>
          <p style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase',
            color: T.gold, marginBottom: 6, fontFamily: FONTS.body,
          }}>Occupancy Rate</p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontFamily: FONTS.display, fontSize: 54, fontWeight: 700, lineHeight: 1, color: '#fff' }}>
              {pct}%
            </span>
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', fontFamily: FONTS.body }}>
              {counts.occupied || 0} / {total} rooms
            </span>
          </div>
        </div>

        {/* Bar + legend */}
        <div style={{ flex: 1, minWidth: 200, paddingTop: 4 }}>
          {/* Stacked proportional bar */}
          <div style={{
            height: 7, borderRadius: 99, overflow: 'hidden', display: 'flex',
            background: 'rgba(255,255,255,.1)', marginBottom: 14,
          }}>
            {Object.entries(STATUS_META).map(([s, meta]) => {
              const p = total > 0 ? ((counts[s] || 0) / total) * 100 : 0;
              return p > 0 ? (
                <div key={s} title={`${meta.label}: ${counts[s]}`}
                  style={{ width: `${p}%`, background: meta.color, transition: 'width .6s ease' }} />
              ) : null;
            })}
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', gap: '6px 18px', flexWrap: 'wrap' }}>
            {Object.entries(STATUS_META).map(([s, meta]) => (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,.55)', fontFamily: FONTS.body }}>
                  {counts[s] || 0} {meta.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Room Card ─────────────────────────────────────────────────────────────────
function RoomCard({ room, rt, allImgs, busy, onStatus, onImgModal, onLightbox }) {
  const [imgIdx, setImgIdx]   = useState(0);
  const [hovered, setHovered] = useState(false);
  const meta   = STATUS_META[room.status] || STATUS_META.available;
  const hasImgs = allImgs && allImgs.length > 0;

  useEffect(() => {
    if (!hovered || !hasImgs || allImgs.length < 2) return;
    const id = setInterval(() => setImgIdx(i => (i + 1) % allImgs.length), 3000);
    return () => clearInterval(id);
  }, [hovered, hasImgs, allImgs?.length]);

  return (
    <div
      className="room-card"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 16, overflow: 'hidden', background: T.white,
        border: `1.5px solid ${hovered ? meta.color + '55' : T.border}`,
        boxShadow: hovered ? `0 8px 28px ${meta.color}22, 0 2px 6px rgba(0,0,0,.06)` : SHADOW.sm,
        transition: 'all .22s cubic-bezier(.4,0,.2,1)',
        transform: hovered ? 'translateY(-4px)' : 'none',
        opacity: busy ? .8 : 1,
      }}>

      {/* ── IMAGE / COLOUR PANEL ─── */}
      <div style={{ position: 'relative', height: 148, overflow: 'hidden' }}>
        {/* Status colour stripe at top */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: meta.color, zIndex: 3 }} />

        {/* Background: image or stylised gradient */}
        {hasImgs ? (
          <div
            onClick={() => onLightbox({ images: allImgs, index: imgIdx })}
            style={{
              position: 'absolute', inset: 0, cursor: 'zoom-in',
              backgroundImage: `url(${allImgs[imgIdx]})`,
              backgroundSize: 'cover', backgroundPosition: 'center',
            }} />
        ) : (
          <div style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(145deg, ${meta.bg} 0%, ${meta.color}10 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {/* Ghost room number as decorative backdrop */}
            <span style={{
              fontFamily: FONTS.display, fontSize: 88, fontWeight: 900,
              color: meta.color, opacity: 0.10, lineHeight: 1, userSelect: 'none',
            }}>{room.roomNumber}</span>
          </div>
        )}

        {/* Dark scrim for images */}
        {hasImgs && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(0deg,rgba(0,0,0,.62) 0%,rgba(0,0,0,.08) 55%,transparent 100%)',
            zIndex: 1,
          }} />
        )}

        {/* Status badge — top left */}
        <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 4 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: meta.color, borderRadius: 99,
            padding: '3px 10px 3px 8px',
            boxShadow: '0 1px 5px rgba(0,0,0,.22)',
          }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'rgba(255,255,255,.75)' }} />
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: 0.8,
              color: '#fff', textTransform: 'uppercase', fontFamily: FONTS.body,
            }}>{meta.label}</span>
          </div>
        </div>

        {/* HK warning — top right */}
        {room.hasOpenTask && (
          <div style={{ position: 'absolute', top: 12, right: 10, zIndex: 4 }}>
            <div style={{
              background: T.warning, color: '#fff', borderRadius: 7,
              padding: '3px 8px', fontSize: 9, fontWeight: 700,
              letterSpacing: 0.5, fontFamily: FONTS.body,
            }}>HK</div>
          </div>
        )}

        {/* Room number — bottom left */}
        <div style={{ position: 'absolute', bottom: 10, left: 13, zIndex: 4 }}>
          <div style={{
            fontFamily: FONTS.display, fontSize: 32, fontWeight: 900, lineHeight: 1,
            color: hasImgs ? '#fff' : meta.color,
            textShadow: hasImgs ? '0 2px 10px rgba(0,0,0,.75)' : 'none',
          }}>{room.roomNumber}</div>
          {room.floor && (
            <div style={{
              fontSize: 10, fontWeight: 600, marginTop: 1, fontFamily: FONTS.body,
              color: hasImgs ? 'rgba(255,255,255,.65)' : `${meta.color}AA`,
              textShadow: hasImgs ? '0 1px 3px rgba(0,0,0,.6)' : 'none',
            }}>
              Floor {room.floor}{room.wing ? ` · ${room.wing}` : ''}
            </div>
          )}
        </div>

        {/* Image carousel dots */}
        {hasImgs && allImgs.length > 1 && (
          <div style={{ position: 'absolute', bottom: 12, right: 10, zIndex: 4, display: 'flex', gap: 4 }}>
            {allImgs.map((_, i) => (
              <button key={i} onClick={e => { e.stopPropagation(); setImgIdx(i); }}
                style={{
                  width: i === imgIdx ? 16 : 5, height: 5, borderRadius: 99,
                  background: i === imgIdx ? '#fff' : 'rgba(255,255,255,.42)',
                  border: 'none', cursor: 'pointer', padding: 0, transition: 'all .25s',
                }} />
            ))}
          </div>
        )}

        {/* Manage photos hover overlay */}
        <div className="room-actions" style={{
          position: 'absolute', inset: 0, opacity: 0, transition: 'opacity .2s', zIndex: 5,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
          padding: '42px 10px 0',
        }}>
          <button onClick={e => { e.stopPropagation(); onImgModal(room); }}
            style={{
              background: 'rgba(0,0,0,.48)', backdropFilter: 'blur(6px)',
              border: '1px solid rgba(255,255,255,.28)',
              borderRadius: 8, padding: '5px 10px', fontSize: 10,
              color: 'white', cursor: 'pointer', fontFamily: FONTS.body, fontWeight: 600,
            }}>
            📷 {hasImgs ? `${allImgs.length} photos` : 'Add photos'}
          </button>
        </div>
      </div>

      {/* ── INFO PANEL ─── */}
      <div style={{ padding: '12px 14px 14px' }}>

        {/* Room type */}
        {rt ? (
          <div style={{ marginBottom: 8 }}>
            <p style={{
              fontSize: 12, fontWeight: 700, color: T.navy,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontFamily: FONTS.body,
            }}>
              {rt.code && <span style={{ color: T.muted, fontWeight: 600 }}>[{rt.code}] </span>}
              {rt.name}
            </p>
            <div style={{ display: 'flex', gap: 10, marginTop: 3, flexWrap: 'wrap' }}>
              {rt.capacity && (
                <span style={{ fontSize: 10, color: T.muted }}>
                  👤 {rt.capacity} guest{rt.capacity !== 1 ? 's' : ''}
                </span>
              )}
              {rt.baseRate && (
                <span style={{ fontSize: 10, color: T.teal, fontWeight: 700 }}>
                  From {Number(rt.baseRate).toLocaleString()}/night
                </span>
              )}
            </div>
          </div>
        ) : (
          <div style={{ height: 38 }} />
        )}

        {/* Guest name when occupied */}
        {room.status === 'occupied' && room.guestName && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 7,
            background: `${T.navy}0C`, borderRadius: 8,
            padding: '6px 10px', marginBottom: 10,
          }}>
            <div style={{
              width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
              background: T.navy, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: 10, color: T.gold, fontWeight: 700 }}>
                {(room.guestName[0] || '?').toUpperCase()}
              </span>
            </div>
            <span style={{
              fontSize: 11, fontWeight: 600, color: T.navy,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{room.guestName}</span>
          </div>
        )}

        {/* Status quick-change — 2 × 3 grid of tap targets */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4 }}>
          {Object.entries(STATUS_META).map(([s, meta]) => {
            const isActive = s === room.status;
            return (
              <button
                key={s}
                onClick={() => !busy && !isActive && onStatus(room.roomId, s)}
                disabled={busy || isActive}
                title={meta.label}
                style={{
                  padding: '6px 4px', borderRadius: 8,
                  border: `1.5px solid ${isActive ? meta.color : meta.color + '28'}`,
                  background: isActive ? meta.color : 'transparent',
                  color: isActive ? '#fff' : meta.color,
                  fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5,
                  cursor: busy || isActive ? 'default' : 'pointer',
                  transition: 'all .15s', fontFamily: FONTS.body,
                  opacity: busy && !isActive ? 0.55 : 1,
                }}>
                {meta.short}
              </button>
            );
          })}
        </div>

        {busy && (
          <p style={{ fontSize: 9, color: T.muted, textAlign: 'center', marginTop: 6, fontFamily: FONTS.body }}>
            Updating…
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function RoomStatusBoard({ property }) {
  const [rooms,      setRooms]      = useState([]);
  const [roomTypes,  setRoomTypes]  = useState({});
  const [roomImages, setRoomImages] = useState({});
  const [loading,    setLoading]    = useState(true);
  const [loadError,  setLoadError]  = useState('');
  const [updating,   setUpdating]   = useState(null);
  const [filter,     setFilter]     = useState('all');
  const [search,     setSearch]     = useState('');
  const [groupFloor, setGroupFloor] = useState(false);
  const [toast,      setToast]      = useState('');
  const [imgModal,   setImgModal]   = useState(null);
  const [lightbox,   setLightbox]   = useState(null);
  const pid = property?.id;

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    setLoadError('');
    let roomList = [];
    try {
      const [board, types] = await Promise.all([fetchRoomBoard(pid), fetchRoomTypes(pid)]);
      roomList = Array.isArray(board) ? board : [];
      setRooms(roomList);
      const map = {};
      (Array.isArray(types) ? types : []).forEach(t => { map[t.id] = t; });
      setRoomTypes(map);
      const seeded = {};
      roomList.forEach(r => {
        const parsed = parseImages(r.images);
        if (parsed.length) seeded[r.roomId] = parsed;
      });
      setRoomImages(seeded);
    } catch (e) {
      console.error('[RoomStatusBoard] load failed:', e);
      setLoadError('Room status is not available offline. Connect to load the latest room data, or wait — it will reload when you reconnect.');
    } finally { setLoading(false); }

    if (!roomList.length) return;
    Promise.allSettled(
      roomList.map(r =>
        fetchRoomImages(r.roomId)
          .then(res => ({ roomId: r.roomId, imgs: res?.images || [] }))
          .catch(() => ({ roomId: r.roomId, imgs: [] }))
      )
    ).then(fetched => {
      const imgMap = {};
      roomList.forEach(r => {
        const parsed = parseImages(r.images);
        if (parsed.length) imgMap[r.roomId] = parsed;
      });
      fetched.forEach(result => {
        if (result.status === 'fulfilled' && result.value.imgs.length)
          imgMap[result.value.roomId] = result.value.imgs;
      });
      setRoomImages(imgMap);
    });
  }, [pid]);

  useOfflineInvalidation('rooms', load);
  useEffect(() => { load(); }, [load]);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleStatus = async (roomId, status) => {
    setUpdating(roomId);
    try {
      const r = await updateRoomStatus(roomId, status);
      showToast(r?._offline ? '⏳ Status saved offline' : `Room updated → ${status}`);
      load();
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setUpdating(null); }
  };

  const handleImagesUpdate = (roomId, updatedImages) => {
    setRoomImages(prev => ({ ...prev, [roomId]: updatedImages }));
  };

  // Filtered + searched rooms
  const filtered = useMemo(() => {
    let list = rooms;
    if (filter !== 'all') list = list.filter(r => r.status === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(r =>
        r.roomNumber?.toString().includes(q) ||
        r.guestName?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [rooms, filter, search]);

  // Optional floor-grouped view
  const floorGroups = useMemo(() => {
    if (!groupFloor) return null;
    const map = {};
    filtered.forEach(r => {
      const key = r.floor != null ? r.floor : '__other__';
      if (!map[key]) map[key] = [];
      map[key].push(r);
    });
    return Object.entries(map).sort(([a], [b]) => {
      if (a === '__other__') return 1;
      if (b === '__other__') return -1;
      return Number(a) - Number(b);
    }).map(([k, v]) => ({ label: k === '__other__' ? 'Other' : `Floor ${k}`, rooms: v }));
  }, [filtered, groupFloor]);

  const counts = useMemo(() => Object.keys(STATUS_META).reduce((a, s) => {
    a[s] = rooms.filter(r => r.status === s).length; return a;
  }, {}), [rooms]);

  const GRID = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(238px,1fr))', gap: 16 };

  const renderCard = room => (
    <RoomCard
      key={room.roomId}
      room={room}
      rt={roomTypes[room.roomTypeId]}
      allImgs={(roomImages[room.roomId] || parseImages(room.images)).length
        ? (roomImages[room.roomId] || parseImages(room.images))
        : parseImages(roomTypes[room.roomTypeId]?.images)}
      busy={updating === room.roomId}
      onStatus={handleStatus}
      onImgModal={setImgModal}
      onLightbox={setLightbox}
    />
  );

  return (
    <div className="fade-up">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes shimmer {
          0%   { background-position: -400px 0; }
          100% { background-position:  400px 0; }
        }
        .shimmer {
          background: linear-gradient(90deg,#f0f4f8 25%,#e2e8f0 50%,#f0f4f8 75%);
          background-size: 400px 100%;
          animation: shimmer 1.4s infinite;
        }
        .room-card:hover .room-actions { opacity: 1 !important; }
        .status-btn:hover:not(:disabled) { filter: brightness(1.08); }
      `}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 70, right: 24, zIndex: 999,
          background: T.navy, color: T.white, borderRadius: 10,
          padding: '11px 18px', fontSize: 13, fontWeight: 600,
          boxShadow: SHADOW.lg, fontFamily: FONTS.body,
        }}>{toast}</div>
      )}

      {imgModal && (
        <ImageManagerModal
          room={imgModal}
          roomType={roomTypes[imgModal.roomTypeId]}
          onClose={() => setImgModal(null)}
          onUpdate={handleImagesUpdate}
        />
      )}

      {lightbox && (
        <Lightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />
      )}

      {/* ── PAGE HEADER ── */}
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontFamily: FONTS.display, fontSize: 30, fontWeight: 700, color: T.navy, lineHeight: 1.1 }}>
          Room Status Board
        </h1>
        <p style={{ fontSize: 12, color: T.muted, marginTop: 4, fontFamily: FONTS.body }}>
          {property?.name}
          {rooms.length > 0 && ` · ${rooms.length} rooms`}
          {!loading && !loadError && ' · Click photos to preview · hover card to manage images'}
        </p>
      </div>

      {/* ── OCCUPANCY SUMMARY ── */}
      {!loading && !loadError && rooms.length > 0 && (
        <OccupancySummary rooms={rooms} />
      )}

      {/* ── SEARCH + FILTER BAR ── */}
      <div style={{ marginBottom: 20 }}>
        {/* Row 1: search + view controls */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 300 }}>
            <span style={{
              position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)',
              color: T.muted, fontSize: 14, pointerEvents: 'none',
            }}>🔍</span>
            <input
              type="text"
              placeholder="Room number or guest…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px 8px 32px',
                border: `1.5px solid ${search ? T.teal : T.border}`,
                borderRadius: 10, fontSize: 12, fontFamily: FONTS.body,
                background: T.white, color: T.navy, outline: 'none',
                transition: 'border-color .15s',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            {/* Floor grouping toggle */}
            <button onClick={() => setGroupFloor(g => !g)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: groupFloor ? T.navy : T.white,
              color: groupFloor ? T.white : T.slate,
              border: `1.5px solid ${groupFloor ? T.navy : T.border}`,
              borderRadius: 9, padding: '7px 14px', fontSize: 12,
              fontWeight: 600, cursor: 'pointer', fontFamily: FONTS.body,
              transition: 'all .15s',
            }}>
              <span style={{ fontSize: 13 }}>⊞</span>
              By Floor
            </button>

            {/* Refresh */}
            <button onClick={load} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: T.teal, border: 'none', borderRadius: 9,
              padding: '7px 16px', fontSize: 12, color: T.white,
              cursor: 'pointer', fontFamily: FONTS.body, fontWeight: 600,
            }}>↺ Refresh</button>
          </div>
        </div>

        {/* Row 2: status filter pills */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button onClick={() => setFilter('all')} style={{
            background: filter === 'all' ? T.navy : T.white,
            color: filter === 'all' ? T.white : T.muted,
            border: `1.5px solid ${filter === 'all' ? T.navy : T.border}`,
            borderRadius: 99, padding: '6px 16px', fontSize: 12, fontWeight: 600,
            cursor: 'pointer', fontFamily: FONTS.body, transition: 'all .15s',
          }}>
            All · {rooms.length}
          </button>
          {Object.entries(STATUS_META).map(([s, meta]) => (
            <button key={s} onClick={() => setFilter(s)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: filter === s ? `${meta.color}14` : T.white,
              color: filter === s ? meta.color : T.muted,
              border: `1.5px solid ${filter === s ? meta.color + '55' : T.border}`,
              borderRadius: 99, padding: '6px 14px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: FONTS.body, transition: 'all .15s',
            }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: meta.color }} />
              {meta.label} · {counts[s] || 0}
            </button>
          ))}
        </div>
      </div>

      {/* ── ROOM GRID ── */}
      {loading ? (
        <div style={GRID}>
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} style={{ height: 310, borderRadius: 16, background: T.border }} className="shimmer" />
          ))}
        </div>
      ) : loadError ? (
        <div style={{
          background: T.surface, border: `2px dashed ${T.border}`, borderRadius: 16,
          padding: 48, textAlign: 'center', maxWidth: 480, margin: '0 auto',
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📡</div>
          <p style={{ fontSize: 14, color: T.slate, lineHeight: 1.65, fontFamily: FONTS.body }}>
            {loadError}
          </p>
          <button onClick={load} style={{
            marginTop: 20, padding: '10px 24px', background: T.teal, color: T.white,
            border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700,
            cursor: 'pointer', fontFamily: FONTS.body,
          }}>↺ Try Again</button>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          background: T.surface, border: `2px dashed ${T.border}`,
          borderRadius: 16, padding: 48, textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏨</div>
          <p style={{ fontSize: 14, color: T.slate, fontFamily: FONTS.body }}>
            {search ? `No rooms match "${search}"` : 'No rooms match this filter'}
          </p>
          {search && (
            <button onClick={() => setSearch('')} style={{
              marginTop: 12, padding: '7px 18px', background: T.white, color: T.navy,
              border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 12,
              cursor: 'pointer', fontFamily: FONTS.body,
            }}>Clear search</button>
          )}
        </div>
      ) : floorGroups ? (
        /* Floor-grouped layout */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {floorGroups.map(({ label, rooms: floorRooms }) => (
            <div key={label}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                <span style={{ fontFamily: FONTS.display, fontSize: 19, fontWeight: 700, color: T.navy, whiteSpace: 'nowrap' }}>
                  {label}
                </span>
                <span style={{ fontSize: 11, color: T.muted, fontFamily: FONTS.body, fontWeight: 600 }}>
                  {floorRooms.length} room{floorRooms.length !== 1 ? 's' : ''}
                </span>
                <div style={{ flex: 1, height: 1, background: T.border }} />
              </div>
              <div style={GRID}>{floorRooms.map(renderCard)}</div>
            </div>
          ))}
        </div>
      ) : (
        /* Normal grid */
        <div style={GRID}>{filtered.map(renderCard)}</div>
      )}
    </div>
  );
}
